import React, { memo, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Entypo } from '@expo/vector-icons';
import { fontFamily } from '../../../helpers/fontFamily';

const FilterDropdown = memo(({
  label = 'Filter',
  options = [],
  selectedValue,
  onSelect,
  testID,
  isOpen: controlledIsOpen,
  onToggle
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleToggle = useCallback(() => {
    if (isControlled) {
      onToggle?.(!isOpen);
    } else {
      setInternalIsOpen(prev => !prev);
    }
  }, [isControlled, isOpen, onToggle]);

  const handleSelect = useCallback((option) => {
    if (isControlled) {
      onToggle?.(false);
    } else {
      setInternalIsOpen(false);
    }
    onSelect?.(option.value);
  }, [onSelect, isControlled, onToggle]);

  const handleClose = useCallback(() => {
    if (isControlled) {
      onToggle?.(false);
    } else {
      setInternalIsOpen(false);
    }
  }, [isControlled, onToggle]);

  const selectedLabel = options.find(opt => opt.value === selectedValue)?.label || label;

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.dropdownButton}
        onPress={handleToggle}
        testID={testID}
      >
        <View style={styles.buttonContent}>
          <Text style={styles.buttonText}>
            {selectedLabel}
          </Text>
          <Entypo
            name="chevron-down"
            size={14}
            color="#fff"
            style={styles.chevronIcon}
          />
        </View>
      </TouchableOpacity>

      {isOpen && (
        <>
          <TouchableOpacity
            style={styles.overlay}
            activeOpacity={1}
            onPress={handleClose}
          />
          <View style={styles.dropdownMenu}>
            {options.map((item) => (
              <TouchableOpacity
                key={item.value}
                style={[
                  styles.optionItem,
                  selectedValue === item.value && styles.optionItemSelected
                ]}
                onPress={() => handleSelect(item)}
                testID={`${testID}-option-${item.value}`}
              >
                <Text style={[
                  styles.optionText,
                  selectedValue === item.value && styles.optionTextSelected
                ]}>
                  {item.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </>
      )}
    </View>
  );
});

FilterDropdown.displayName = 'FilterDropdown';

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 99999,
    marginRight: 10
  },
  dropdownButton: {
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#fff',
    borderRadius: 30,
    height: 22,
    paddingHorizontal: 10
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  buttonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 12
  },
  chevronIcon: {
    marginLeft: 5
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99998
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 5,
    backgroundColor: 'rgb(38, 43, 48)',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#80808082',
    minWidth: 120,
    zIndex: 99999
  },
  optionItem: {
    padding: 10
  },
  optionItemSelected: {
    backgroundColor: 'rgba(123, 250, 195, 0.1)'
  },
  optionText: {
    color: '#fff',
    fontFamily: fontFamily.font300,
    fontSize: 14
  },
  optionTextSelected: {
    color: '#7bfac3'
  }
});

export default FilterDropdown;

